import {
  Stack,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  useTheme,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
// components
import Iconify from "../../../components/iconify";
import ExportIcon from "../../../assets/icons/ExportIcon";
import { useState } from "react";
import { saveAs } from "file-saver";
import { useSnackbar } from "notistack";
import { IRecall } from "../../../@types/recalls";
import { useAuthContext } from "../../../auth/useAuthContext";

// ----------------------------------------------------------------------

type statusOptionsProps = {
  label: string;
  value: string;
};

type Props = {
  isFiltered: boolean;
  filterName: string;
  selectedDate: Date | null;
  filterStage: string;
  recalls: IRecall[];
  onFilterName: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFilterStage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDateChange: (newDate: Date | null) => void;
  onResetFilter: VoidFunction;
};


const STAGE_OPTIONS = [
  { value: "all", label: "All Stages" },
  { value: "1", label: "Stage 1 (SMS)" },
  { value: "2", label: "Stage 2 (Email)" },
  { value: "3", label: "Stage 3 (Phone Call)" },
];

export default function RecallTableToolbar({
  isFiltered,
  filterName,
  selectedDate,
  filterStage,
  recalls,
  onFilterName,
  onFilterStage,
  onDateChange,
  onResetFilter,
}: Props) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  const exportList = async () => {
    try {
      setExportLoading(true);

      // Create CSV header
      const headers = "Patient Name,Recall Type,Stage,Status,Last Exam Date,Recall Date,Scheduled Date";

      // Create CSV rows from actual recall data
      const rows = recalls.map((recall) => {
        const formatDate = (dateStr: string | Date | undefined) => {
          if (dateStr == null || dateStr === "") return "-";
          try {
            const d = new Date(dateStr);
            if (Number.isNaN(d.getTime()) || d.getTime() === 0) return "-";
            return format(d, "dd/MM/yyyy");
          } catch {
            return "-";
          }
        };

        const stageName = recall.stage === 1 ? "SMS" : recall.stage === 2 ? "Email" : "Phone Call";

        return [
          recall.patientName || "N/A",
          recall.recallType || "N/A",
          stageName,
          recall.status || "Scheduled",
          formatDate(recall.lastExamDate),
          formatDate(recall.recallDate),
          formatDate(recall.scheduledDate),
        ].join(",");
      });

      const csvContent = [headers, ...rows].join("\n");

      const blobFile = new Blob([csvContent], {
        type: "text/csv;charset=utf-8",
      });
      saveAs(blobFile, `RecallRecords_${format(new Date(), "yyyy-MM-dd")}.csv`);
      setExportLoading(false);
      enqueueSnackbar("Export successful!", {
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
      });
    } catch (error: any) {
      setExportLoading(false);
      enqueueSnackbar(error?.message || "Something went wrong!", {
        variant: "error",
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
      });
    }
  };
  return (
    <Stack
      spacing={2}
      alignItems="center"
      direction={{
        xs: "column",
        sm: "row",
      }}
      sx={{ px: 2.5, py: 3 }}
    >
      <TextField
        fullWidth
        value={filterName}
        onChange={onFilterName}
        placeholder="Search Patient by Name"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" sx={{ color: "text.disabled" }} />
            </InputAdornment>
          ),
        }}
      />

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label="Up-to-Date"
          value={selectedDate}
          onChange={onDateChange}
          slotProps={{
            textField: {
              sx: {
                width: { sm: 400 },
              },
            },
          }}
        />
      </LocalizationProvider>

      <TextField
        select
        label="Stage"
        value={filterStage}
        onChange={onFilterStage}
        SelectProps={{
          MenuProps: {
            sx: { "& .MuiPaper-root": { maxHeight: 260 } },
          },
        }}
        sx={{
          width: { sm: 400 },
          textTransform: "capitalize",
        }}
      >
        {STAGE_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{
              mx: 1,
              borderRadius: 0.75,
              typography: "body2",
              textTransform: "capitalize",
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      {exportLoading ? (
        <IconButton
          color="default"
          sx={{
            p: 0,
            backgroundColor: "white",
            width: "40px",
            height: "40px",
            borderRadius: "15px",
          }}>
          <CircularProgress size={24} color="inherit" />
        </IconButton>
      ) : (
        <Tooltip title="Export List">
          <IconButton color="default" onClick={exportList} sx={{ p: 0 }}>
            <ExportIcon />
          </IconButton>
        </Tooltip>
      )}

      {filterName && (
        <Button color="error" onClick={onResetFilter}>
          Cancel
        </Button>
      )}
    </Stack>
  );
}